import { Controller } from 'stimulus'

const MOBILE_QUERY = '(max-width: 768px)'
const ROTATION_INTERVAL = 5000

/**
 * Manages the sitewide USP banner rotation (StimulusJS controller)
 *
 * @class UspBannerController
 * @memberof Controllers
 *
 */
export default class extends Controller {
  static targets = [ 'slide' ]

  /**
   * @function Controllers.UspBannerController.connect
   * @memberof Controllers
   * @description Sets up media query listener and starts rotation on mobile
   *
   * @return {undefined} Manipulates DOM
   *
   */
  connect() {
    this.currentIndex = 0
    this.intervalId = null
    this.touchStartX = null
    this.touchCurrentX = null
    this.mql = window.matchMedia(MOBILE_QUERY)
    this.handleMediaChange = this.handleMediaChange.bind(this)
    this.boundTouchStart = this.onTouchStart.bind(this)
    this.boundTouchMove = this.onTouchMove.bind(this)
    this.boundTouchEnd = this.onTouchEnd.bind(this)
    this.boundTouchCancel = this.onTouchCancel.bind(this)
    this.element.addEventListener('touchstart', this.boundTouchStart, { passive: true })
    this.element.addEventListener('touchmove', this.boundTouchMove, { passive: true })
    this.element.addEventListener('touchend', this.boundTouchEnd)
    this.element.addEventListener('touchcancel', this.boundTouchCancel)
    this.mql.addEventListener('change', this.handleMediaChange)
    this.handleMediaChange(this.mql)
  }

  /**
   * @function Controllers.UspBannerController.disconnect
   * @memberof Controllers
   * @description Stops rotation and removes media query listener
   *
   * @return {undefined} Manipulates DOM
   *
   */
  disconnect() {
    this.stopRotation()
    this.mql.removeEventListener('change', this.handleMediaChange)
    this.element.removeEventListener('touchstart', this.boundTouchStart)
    this.element.removeEventListener('touchmove', this.boundTouchMove)
    this.element.removeEventListener('touchend', this.boundTouchEnd)
    this.element.removeEventListener('touchcancel', this.boundTouchCancel)
  }

  /**
   * @function Controllers.UspBannerController.handleMediaChange
   * @memberof Controllers
   * @description Starts or stops rotation based on mobile breakpoint match
   *
   * @param {MediaQueryListEvent} event - Media query change event
   *
   * @return {undefined} Manipulates DOM
   *
   */
  handleMediaChange(event) {
    if (event.matches) {
      this.startRotation()
    } else {
      this.stopRotation()
      this.showAll()
    }
  }

  /**
   * @function Controllers.UspBannerController.startRotation
   * @memberof Controllers
   * @description Begins auto-rotating slides every 5 seconds
   *
   * @return {undefined} Manipulates DOM
   *
   */
  startRotation() {
    if (this.intervalId) return
    this.updateDisplay()
    this.intervalId = setInterval(() => this.advance(), ROTATION_INTERVAL)
  }

  /**
   * @function Controllers.UspBannerController.stopRotation
   * @memberof Controllers
   * @description Clears the rotation interval
   *
   * @return {undefined} Manipulates DOM
   *
   */
  stopRotation() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  /**
   * @function Controllers.UspBannerController.showAll
   * @memberof Controllers
   * @description Removes active class from all slides (desktop state)
   *
   * @return {undefined} Manipulates DOM
   *
   */
  showAll() {
    this.slideTargets.forEach(slide => {
      slide.classList.remove('usp-banner__slide--active')
      slide.classList.remove('usp-banner__slide--prev')
    })
  }

  /**
   * @function Controllers.UspBannerController.advance
   * @memberof Controllers
   * @description Increments the current slide index and updates display
   *
   * @return {undefined} Manipulates DOM
   *
   */
  advance() {
    this.currentIndex = (this.currentIndex + 1) % this.slideTargets.length
    this.updateDisplay()
  }

  /**
   * @function Controllers.UspBannerController.updateDisplay
   * @memberof Controllers
   * @description Toggles active class on slides to show current index
   *
   * @return {undefined} Manipulates DOM
   *
   */
  updateDisplay() {
    const total = this.slideTargets.length
    const prevIndex = (this.currentIndex - 1 + total) % total
    this.slideTargets.forEach((slide, i) => {
      const willBeActive = i === this.currentIndex
      const willBePrev = i === prevIndex
      const wasPrev = slide.classList.contains('usp-banner__slide--prev')

      // Suppress transition for a slide losing --prev (not becoming active),
      // so it jumps instantly off-screen rather than traveling through the viewport.
      if (wasPrev && !willBePrev && !willBeActive) {
        slide.style.transition = 'none'
        requestAnimationFrame(() => { slide.style.transition = '' })
      }

      slide.classList.toggle('usp-banner__slide--active', willBeActive)
      slide.classList.toggle('usp-banner__slide--prev', willBePrev)
    })
  }

  /**
   * @function Controllers.UspBannerController.onTouchStart
   * @memberof Controllers
   * @description Records the starting X position of a touch gesture
   *
   * @param {TouchEvent} e - Touch start event
   *
   * @return {undefined}
   *
   */
  onTouchStart(e) {
    if (!this.mql.matches) return
    this.touchStartX = e.touches[0].clientX
    this.touchCurrentX = this.touchStartX
  }

  /**
   * @function Controllers.UspBannerController.onTouchMove
   * @memberof Controllers
   * @description Applies live inline transforms to slides as the user drags
   *
   * @param {TouchEvent} e - Touch move event
   *
   * @return {undefined} Manipulates DOM
   *
   */
  onTouchMove(e) {
    if (!this.mql.matches || this.touchStartX === null) return
    this.touchCurrentX = e.touches[0].clientX
    const delta = this.touchCurrentX - this.touchStartX
    const total = this.slideTargets.length
    const prevIndex = (this.currentIndex - 1 + total) % total
    const nextIndex = (this.currentIndex + 1) % total
    this.slideTargets.forEach((slide, i) => {
      slide.style.transition = 'none'
      if (i === this.currentIndex) {
        slide.style.transform = `translateX(${delta}px)`
      } else if (i === nextIndex) {
        slide.style.transform = `translateX(calc(100% + ${delta}px))`
      } else if (i === prevIndex) {
        slide.style.transform = `translateX(calc(-100% + ${delta}px))`
      }
    })
  }

  /**
   * @function Controllers.UspBannerController.onTouchEnd
   * @memberof Controllers
   * @description Snaps to the next/previous slide or back on swipe release
   *
   * @param {TouchEvent} e - Touch end event
   *
   * @return {undefined} Manipulates DOM
   *
   */
  onTouchEnd() {
    if (this.touchStartX === null) return
    const delta = this.touchCurrentX - this.touchStartX
    const threshold = 40
    this.slideTargets.forEach(slide => {
      slide.style.transition = ''
      slide.style.transform = ''
    })
    if (delta < -threshold) {
      this.currentIndex = (this.currentIndex + 1) % this.slideTargets.length
      this.pauseAndResume()
    } else if (delta > threshold) {
      this.currentIndex = (this.currentIndex - 1 + this.slideTargets.length) % this.slideTargets.length
      this.pauseAndResume()
    }
    this.updateDisplay()
    this.touchStartX = null
    this.touchCurrentX = null
  }

  /**
   * @function Controllers.UspBannerController.onTouchCancel
   * @memberof Controllers
   * @description Cleans up inline styles if the OS cancels a touch gesture mid-drag
   *
   * @return {undefined} Manipulates DOM
   *
   */
  onTouchCancel() {
    if (this.touchStartX === null) return
    this.slideTargets.forEach(slide => {
      slide.style.transition = ''
      slide.style.transform = ''
    })
    this.touchStartX = null
    this.touchCurrentX = null
  }

  /**
   * @function Controllers.UspBannerController.pauseAndResume
   * @memberof Controllers
   * @description Stops auto-rotation after a manual swipe and resumes after two intervals
   *
   * @return {undefined}
   *
   */
  pauseAndResume() {
    this.stopRotation()
    setTimeout(() => {
      if (this.mql.matches) this.startRotation()
    }, ROTATION_INTERVAL * 2)
  }
}
